import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { founderApi } from '@/lib/founderApi';
import { usePageMeta } from '@/lib/usePageMeta';
import { RefundNotice } from '@/components/digital/RefundNotice';
import { BlueprintBuyPanel } from '@/components/founder/sales/BlueprintBuyPanel';
import { SALES_STAGES, SALES_INCLUDES, SALES_AUDIENCE } from '@/components/founder/sales/BlueprintSalesContent';

const wrap = 'mx-auto max-w-[1440px] px-6 sm:px-10 lg:px-16';

const Stages = () => (
  <section className="py-24 lg:py-32 border-t border-taupe/30" data-testid="fb-sales-stages">
    <div className={wrap}>
      <div className="eyebrow !text-gold mb-6">Six interactive stages</div>
      <h2 className="font-serif-display text-4xl md:text-5xl tracking-tighter text-matte max-w-3xl">From first idea to a 30-day launch plan.</h2>
      <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-taupe/30 border border-taupe/30 rounded-2xl overflow-hidden">
        {SALES_STAGES.map((s) => (
          <div key={s.n} className="bg-ivory p-8 lg:p-10 hover:bg-sand/40 transition-colors duration-500" data-testid={`fb-sales-stage-${s.name.toLowerCase()}`}>
            <div className="font-label text-[10px] tracking-widest uppercase text-gold">Stage {s.n}</div>
            <h3 className="mt-4 font-serif-display text-3xl text-matte">{s.name}</h3>
            <p className="mt-4 text-matte/65 leading-relaxed text-[15px]">{s.text}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const Includes = () => (
  <section className="py-24 lg:py-32 bg-sand/40" data-testid="fb-sales-includes">
    <div className={`${wrap} grid lg:grid-cols-12 gap-12`}>
      <div className="lg:col-span-4">
        <div className="eyebrow !text-gold mb-6">What&rsquo;s included</div>
        <h2 className="font-serif-display text-4xl md:text-5xl tracking-tighter text-matte">A guided planning experience you complete, not a file you fill in.</h2>
      </div>
      <div className="lg:col-span-8 grid sm:grid-cols-2 gap-x-10 gap-y-12">
        {SALES_INCLUDES.map((i) => (
          <div key={i.title} className="border-t border-gold/50 pt-6">
            <h3 className="font-serif-display text-2xl text-matte">{i.title}</h3>
            <p className="mt-3 text-matte/65 leading-relaxed text-[15px]">{i.text}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const Audience = () => (
  <section className="py-24 lg:py-32" data-testid="fb-sales-audience">
    <div className={`${wrap} grid lg:grid-cols-12 gap-12`}>
      <div className="lg:col-span-4"><div className="eyebrow !text-gold mb-6">Designed for</div>
        <h2 className="font-serif-display text-4xl md:text-5xl tracking-tighter text-matte">Emerging founders.</h2></div>
      <ul className="lg:col-span-8 divide-y divide-taupe/30 border-y border-taupe/30">
        {SALES_AUDIENCE.map((a) => <li key={a} className="py-6 font-serif-display text-2xl text-matte/85">{a}</li>)}
      </ul>
    </div>
  </section>
);

export default function BlueprintSalesPage() {
  const [params] = useSearchParams();
  const [info, setInfo] = useState(null);
  usePageMeta({ title: 'ORIGENNE Founder Blueprint Builder™', path: '/resources/digital-products/founder-blueprint-builder',
    description: 'An interactive, self-paced business planning experience in six stages, with financial planning, founder decisions, a 30-day launch plan and a personalized PDF. $39 one-time.' });
  useEffect(() => { founderApi.info().then(setInfo).catch(() => setInfo({ sale_mode: 'coming_soon', price_cents: 3900 })); }, []);
  return (
    <div className="bg-ivory" data-testid="fb-sales-page">
      <section className="pt-36 lg:pt-48 pb-24">
        <div className={wrap}>
          <Link to="/resources/digital-products" className="btn-ghost mb-12" data-testid="fb-sales-back">
            <ArrowLeft size={14} strokeWidth={1.6} /> The Library
          </Link>
          <div className="grid lg:grid-cols-12 gap-12 lg:gap-20 items-start">
            <div className="lg:col-span-7 min-w-0">
              <div className="eyebrow !text-gold mb-6">ORIGENNE&trade; · Emerging Founder Collection</div>
              <h1 className="font-serif-display text-5xl md:text-6xl lg:text-7xl leading-[0.98] tracking-tightest text-matte" data-testid="fb-sales-title">
                ORIGENNE Founder Blueprint Builder&trade;
              </h1>
              <p className="mt-8 font-serif-display italic text-2xl text-taupe leading-snug max-w-2xl">
                An interactive, self-paced planning experience that walks you through your business in six stages and turns your answers into a personalized blueprint.
              </p>
              <p className="mt-8 text-matte/70 leading-relaxed text-[15px] max-w-2xl" data-testid="fb-sales-powered-by">
                Founder Blueprint Builder&trade; is an interactive business-planning experience powered by ORIGENNE&trade;, the digital headquarters of Toia Wallace, and the first interactive experience in its Emerging Founder Collection.
              </p>
              <p className="mt-4 text-matte/70 leading-relaxed text-[15px] max-w-2xl">
                You work inside a private Founder Workspace: answering guided questions, running your own numbers, practicing founder decisions and building a 30-day launch plan. When you&rsquo;re done, download your blueprint as a branded PDF.
              </p>
            </div>
            <div className="lg:col-span-5"><BlueprintBuyPanel info={info} cancelled={params.get('checkout') === 'cancelled'} /></div>
          </div>
        </div>
      </section>
      <Stages />
      <Includes />
      <Audience />
      <section className="pb-24 lg:pb-32"><div className={wrap}>
        <p className="max-w-3xl text-matte/60 text-[13px] leading-relaxed border-t border-taupe/30 pt-6" data-testid="fb-sales-disclaimer">
          The Founder Blueprint Builder&trade; is an educational planning tool. Calculations are estimates based only on the numbers you enter. They are not forecasts or guarantees of income, revenue or profit, and the tool does not replace professional financial, legal, tax or business advice.
        </p>
        <RefundNotice className="mt-8 max-w-3xl" />
      </div></section>
    </div>
  );
}
